import {
  AlertTriangle,
  AtSign,
  CheckCircle2,
  Crown,
  Eye,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Search,
  ShieldCheck,
  ShieldX,
  ShoppingCart,
  Trash2,
  UserCheck,
  UserX,
  Wallet,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { adminDeleteUser, godImpersonateUser, listAdminUsers, patchAdminUser, purgeUserPositions } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';
import { useAdmin } from '@/contexts/AdminContext';

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;

// Patch v17.7 — compact signup date renderer (YYYY-MM-DD). ISO input
// might be "2026-07-13T04:59:15.197658+00:00" or plain "" for legacy
// rows; the slice keeps the first 10 chars and handles both safely.
const shortDate = (v) => (v && typeof v === 'string' ? v.slice(0, 10) : '—');

// Patch v17.7 — Impersonation click handler. Mints an impersonation
// token via the backend then opens a NEW browser tab at /app carrying
// the token in the URL. The bootstrap block in /src/index.js reads the
// param, stashes it into sessionStorage, and strips it from the URL.
// The god admin's own admin session in ``localStorage`` is not touched.
const openImpersonationTab = async (user) => {
  try {
    const resp = await godImpersonateUser(user.id);
    const token = resp?.access_token;
    if (!token) {
      toast.error('Failed to mint impersonation token.');
      return;
    }
    const _base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    // Use window.location.origin (not backend URL) — the frontend and
    // backend share a domain via ingress, so /app is served by the SPA.
    const targetUrl = `${window.location.origin}/app?impersonation_token=${encodeURIComponent(token)}`;
    const opened = window.open(targetUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      toast.error('Pop-up blocked — allow pop-ups for this site and retry.');
      return;
    }
    toast.success(`Opened tab as @${user.username}`);
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || 'unknown';
    toast.error(`Impersonation failed: ${detail}`);
  }
};

const StatusBadge = ({ value }) => {
  if (value === 'ACTIVE') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase"
        style={{
          background: 'linear-gradient(180deg, #FFC20E, #FFB612)',
          color: '#0a0a0b',
          border: '1px solid rgba(255,182,18,0.65)',
          boxShadow: '0 0 14px rgba(255,182,18,0.35)',
        }}
        data-testid="user-status-active"
      >
        ACTIVE
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase"
      style={{
        background: 'transparent',
        color: '#FFB612',
        border: '1px solid rgba(255,182,18,0.42)',
      }}
      data-testid="user-status-inactive"
    >
      INACTIVE
    </span>
  );
};

const EditModal = ({ open, user, onClose, onSaved }) => {
  const { admin: actingAdmin } = useAdmin();
  const isGod = Boolean(actingAdmin?.is_god_account);
  const uid = useId();
  const [form, setForm] = useState({
    email: '',
    username: '',
    upline_user_id: '',
    purchase_balance_delta: '',
    withdrawal_balance_delta: '',
    new_password: '',
    status: 'active',
    is_admin: false,
  });
  const [busy, setBusy] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        email: user.email || '',
        username: user.username || '',
        upline_user_id: user.upline_user_id || '0',
        purchase_balance_delta: '',
        withdrawal_balance_delta: '',
        new_password: '',
        status: (user.status || 'active').toLowerCase() === 'suspended' ? 'suspended' : 'active',
        is_admin: Boolean(user.is_admin),
      });
      setConfirmPurge(false);
    }
  }, [open, user]);

  if (!open || !user) return null;

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      if (form.email && form.email !== user.email) body.email = form.email.trim();
      if (form.username && form.username !== user.username) body.username = form.username.trim();
      if (form.upline_user_id.trim() !== String(user.upline_user_id || '0')) body.upline_user_id = form.upline_user_id.trim();
      if (form.purchase_balance_delta) body.purchase_balance_delta = form.purchase_balance_delta.trim();
      if (form.withdrawal_balance_delta) body.withdrawal_balance_delta = form.withdrawal_balance_delta.trim();
      if (form.new_password && form.new_password.length >= 8) body.new_password = form.new_password;
      const currentStatus = (user.status || 'active').toLowerCase() === 'suspended' ? 'suspended' : 'active';
      if (form.status !== currentStatus) body.status = form.status;
      if (isGod && Boolean(form.is_admin) !== Boolean(user.is_admin)) body.is_admin = Boolean(form.is_admin);
      if (Object.keys(body).length === 0) {
        toast.message('No changes to apply');
        return;
      }
      await patchAdminUser(user.id, body);
      toast.success('User updated');
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Update failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    if (!confirmPurge) {
      setConfirmPurge(true);
      return;
    }
    setPurging(true);
    try {
      const r = await purgeUserPositions(user.id);
      toast.success(`Purged ${r.total_removed} positions (filler=${r.removed_filler}, root=${r.removed_root}, completed=${r.removed_completed})`);
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Purge failed: ${msg}`);
    } finally {
      setPurging(false);
      setConfirmPurge(false);
    }
  };

  const deleteUser = async () => {
    if (deleteConfirmText !== user.username) return;
    setDeleting(true);
    try {
      const r = await adminDeleteUser(user.id, deleteConfirmText);
      const removed = (r?.removed_filler || 0) + (r?.removed_root || 0) + (r?.removed_archived || 0);
      toast.success(`User '${r?.deleted_username}' deleted — ${removed} positions + ${r?.removed_subpacks || 0} sub packs removed`);
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Delete failed: ${msg}`);
    } finally {
      setDeleting(false);
      setDeleteConfirmText('');
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="user-edit-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={onClose} aria-hidden="true" />
      <div className="relative sc-glass-strong rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-6 py-5 border-b border-[rgba(255,182,18,0.18)]">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#FFB612]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFC20E]">Administrative Override</div>
            </div>
            <div className="font-display font-bold text-xl mt-1">{user.username}</div>
            <div className="text-xs text-[var(--sc-text-mute)] mt-0.5">id: {user.id}</div>
          </div>
          <StatusBadge value={user.status} />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor={`${uid}-username`}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5"
              >
                <AtSign className="w-3.5 h-3.5" /> Username
              </label>
              <input
                id={`${uid}-username`}
                className="sc-input"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                data-testid="edit-username"
              />
            </div>
            <div>
              <label htmlFor={`${uid}-email`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
                <Mail className="w-3.5 h-3.5" /> Email
              </label>
              <input
                id={`${uid}-email`}
                className="sc-input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="edit-email"
              />
            </div>
          </div>
          <div>
            <label htmlFor={`${uid}-referrer`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              <UserCheck className="w-3.5 h-3.5" /> Referrer (sponsor id / username, or “0” for orphan)
            </label>
            <input
              id={`${uid}-referrer`}
              className="sc-input"
              value={form.upline_user_id}
              onChange={(e) => setForm((f) => ({ ...f, upline_user_id: e.target.value }))}
              placeholder="0"
              data-testid="edit-referrer"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor={`${uid}-purchase-delta`}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5"
              >
                <ShoppingCart className="w-3.5 h-3.5" /> Purchase Balance Δ (e.g. +10.00 or -5.00)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[var(--sc-text-mute)] whitespace-nowrap">
                  Now: <span className="text-[#FFC20E] sc-tabular">{money(user.purchase_balance)}</span>
                </span>
              </div>
              <input
                id={`${uid}-purchase-delta`}
                className="sc-input mt-2"
                value={form.purchase_balance_delta}
                onChange={(e) => setForm((f) => ({ ...f, purchase_balance_delta: e.target.value }))}
                placeholder="+10.00 / -5.00"
                data-testid="edit-purchase-delta"
              />
            </div>
            <div>
              <label
                htmlFor={`${uid}-withdrawal-delta`}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5"
              >
                <Wallet className="w-3.5 h-3.5" /> Withdrawal Balance Δ
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-[var(--sc-text-mute)] whitespace-nowrap">
                  Now: <span className="text-[#FFC20E] sc-tabular">{money(user.withdrawal_balance)}</span>
                </span>
              </div>
              <input
                id={`${uid}-withdrawal-delta`}
                className="sc-input mt-2"
                value={form.withdrawal_balance_delta}
                onChange={(e) => setForm((f) => ({ ...f, withdrawal_balance_delta: e.target.value }))}
                placeholder="+10.00 / -5.00"
                data-testid="edit-withdrawal-delta"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor={`${uid}-new-password`}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5"
              >
                <KeyRound className="w-3.5 h-3.5" /> New Password (min 8 chars, leave blank to keep)
              </label>
              <input
                id={`${uid}-new-password`}
                type="password"
                autoComplete="new-password"
                className="sc-input"
                value={form.new_password}
                onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                placeholder="••••••••"
                data-testid="edit-new-password"
              />
            </div>
            <div>
              <label htmlFor={`${uid}-status`} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
                <ShieldX className="w-3.5 h-3.5" /> Account Status
              </label>
              <select
                id={`${uid}-status`}
                className="sc-input"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                data-testid="edit-status"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          {isGod ? (
            <label
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[rgba(255,182,18,0.24)] bg-[rgba(255,182,18,0.05)] cursor-pointer"
              data-testid="edit-admin-row"
            >
              <input
                type="checkbox"
                className="sc-no-style w-4 h-4 accent-[#FFC20E] cursor-pointer"
                checked={Boolean(form.is_admin)}
                onChange={(e) => setForm((f) => ({ ...f, is_admin: e.target.checked }))}
                data-testid="edit-is-admin"
              />
              <Crown className="w-4 h-4 text-[#FFC20E]" />
              <span className="text-[12px] uppercase tracking-[0.18em] font-bold text-[var(--sc-text)]">Administrator role</span>
              <span className="ml-auto text-[10px] text-[var(--sc-text-dim)]">Grants access to /admin</span>
            </label>
          ) : null}

          <div className="rounded-xl border border-[rgba(248,113,113,0.32)] bg-[rgba(248,113,113,0.06)] p-4" data-testid="danger-zone">
            <div className="flex items-center gap-2 text-[#fca5a5] text-[11px] uppercase tracking-[0.22em] font-bold">
              <AlertTriangle className="w-3.5 h-3.5" />
              Danger Zone
            </div>
            <p className="text-xs text-[var(--sc-text-dim)] mt-2">
              Purge wipes every position (active in the live pool AND completed audit rows) for this user. This operation cannot be undone.
            </p>
            <button
              type="button"
              onClick={purge}
              disabled={purging}
              className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-semibold border transition ${
                confirmPurge
                  ? 'bg-[rgba(248,113,113,0.18)] border-[rgba(248,113,113,0.55)] text-[#fca5a5]'
                  : 'bg-transparent border-[rgba(248,113,113,0.32)] text-[#fca5a5] hover:bg-[rgba(248,113,113,0.12)]'
              }`}
              data-testid="purge-positions-btn"
            >
              {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {confirmPurge ? 'Click again to confirm purge' : 'Purge user positions'}
            </button>

            {/* DELETE USER — fully removes the user and ALL their data */}
            <div className="mt-4 pt-4 border-t border-[rgba(248,113,113,0.22)]">
              <div className="flex items-center gap-2 text-[#fca5a5]">
                <UserX className="w-3.5 h-3.5" />
                <div className="font-display font-semibold text-[12px] uppercase tracking-[0.18em]">Delete user</div>
              </div>
              <p className="text-[11px] text-[var(--sc-text-dim)] mt-1.5 leading-relaxed">
                Permanently removes this user, their sub packs, all their positions (queue + archive), and their ledger entries. Type the username exactly to
                enable the button.
              </p>
              <input
                type="text"
                className="sc-input mt-3"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={user.username}
                data-testid="edit-delete-confirm"
              />
              <button
                type="button"
                onClick={deleteUser}
                disabled={deleting || deleteConfirmText !== user.username}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-semibold border transition"
                style={
                  deleteConfirmText === user.username && !deleting
                    ? {
                        background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)',
                        borderColor: 'rgba(248,113,113,0.65)',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                      }
                    : {
                        background: 'transparent',
                        borderColor: 'rgba(248,113,113,0.20)',
                        color: 'var(--sc-text-mute)',
                        cursor: 'not-allowed',
                      }
                }
                data-testid="edit-delete-user-btn"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" />}
                Delete user permanently
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[rgba(255,182,18,0.18)] flex items-center justify-end gap-2 bg-[rgba(8,8,9,0.6)]">
          <button type="button" onClick={onClose} className="sc-btn-ghost" style={{ padding: '10px 14px', fontSize: 12 }} data-testid="edit-cancel">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={busy} className="sc-btn-gold" style={{ padding: '10px 18px', fontSize: 12 }} data-testid="edit-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
            Save Overrides
          </button>
        </div>
      </div>
    </div>
  );
};

const UsersSubpage = () => {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const [data, setData] = useState({ items: [], page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listAdminUsers(page, q, dateRangeParams(dates));
      setData(d);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, q, dates]);

  useEffect(() => {
    const t = setTimeout(load, 240); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="users-subpage">
      <div className="sc-glass rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <div className="font-display font-bold text-xl">User Accounts Directory</div>
            <p className="text-xs text-[var(--sc-text-dim)] mt-1">
              Full database lookup. Click any row to launch the 6-parameter administrative override modal. Status reflects whether the user holds at least one
              position (active or completed).
            </p>
          </div>
          <div className="relative w-full md:w-[320px]">
            <Search className="w-4 h-4 absolute top-1/2 left-3 -translate-y-1/2 text-[var(--sc-text-mute)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search username, email or id…"
              className="sc-input pl-9"
              data-testid="users-search"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={dates.date_from}
            dateTo={dates.date_to}
            onChange={(range) => {
              setPage(1);
              setDates(range);
            }}
            fromLabel="Signup From"
            toLabel="Signup To"
            testIdPrefix="users-filter"
          />
        </div>
      </div>

      <div className="sc-glass rounded-2xl p-5 overflow-hidden">
        <div className="sc-admin-table-wrap">
          <table className="sc-admin-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Referrer</th>
                <th className="text-right">Purchase</th>
                <th className="text-right">Withdrawal</th>
                <th className="text-right">Positions</th>
                <th>Status</th>
                {/* Patch v17.7 — enhanced per-user stats + god-admin actions */}
                <th className="whitespace-nowrap">Signup</th>
                <th className="text-right whitespace-nowrap" title="How many other users list this user as their upline">
                  Refs
                </th>
                <th className="text-right whitespace-nowrap" title="Lifetime Cycle-type ledger entries for this user">
                  Cycles
                </th>
                <th className="text-right whitespace-nowrap" title="Total Sub Packs the user has ever bought">
                  Packs
                </th>
                <th
                  className="text-right whitespace-nowrap"
                  title="Lifetime positions purchased with source=single (still queued, sidelined, or already cycled)"
                >
                  Singles
                </th>
                <th className="text-center whitespace-nowrap" title="Does the user currently have at least one is_active=true Sub Pack?">
                  Sub Active
                </th>
                <th className="text-center whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && data.items.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-12">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FFB612]" />
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-12 text-[var(--sc-text-mute)]">
                    No users match your filter.
                  </td>
                </tr>
              ) : (
                data.items.map((u) => (
                  <tr key={u.id} onClick={() => setEditing(u)} className="cursor-pointer" data-testid={`user-row-${u.username}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{u.username}</span>
                        {u.is_admin ? (
                          <span className="text-[9px] uppercase tracking-[0.2em] text-[#FFC20E] border border-[rgba(255,182,18,0.5)] px-1 py-0.5 rounded">
                            ADMIN
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="text-xs text-[var(--sc-text-dim)]">{u.email}</td>
                    <td className="text-xs text-[var(--sc-text-dim)]">
                      {u.upline_user_id === '0' ? <span className="text-[var(--sc-text-mute)]">— orphan</span> : u.referrer_username || u.upline_user_id}
                    </td>
                    <td className="text-right sc-tabular text-[#FFC20E]">{money(u.purchase_balance)}</td>
                    <td className="text-right sc-tabular text-[#FFC20E]">{money(u.withdrawal_balance)}</td>
                    <td className="text-right sc-tabular">{u.position_count}</td>
                    <td>
                      <StatusBadge value={u.status} />
                    </td>
                    {/* Patch v17.7 — enhanced stats cells */}
                    <td className="text-xs text-[var(--sc-text-dim)] whitespace-nowrap" data-testid={`user-signup-${u.username}`}>
                      {shortDate(u.created_at)}
                    </td>
                    <td className="text-right sc-tabular text-[var(--sc-text-dim)]" data-testid={`user-refs-${u.username}`}>
                      {u.referral_count ?? 0}
                    </td>
                    <td className="text-right sc-tabular" data-testid={`user-cycles-${u.username}`}>
                      {u.cycles_completed ?? 0}
                    </td>
                    <td className="text-right sc-tabular text-[var(--sc-text-dim)]" data-testid={`user-subpacks-${u.username}`}>
                      {u.sub_packs_total ?? 0}
                    </td>
                    <td className="text-right sc-tabular text-[var(--sc-text-dim)]" data-testid={`user-singles-${u.username}`}>
                      {u.single_positions_purchased ?? 0}
                    </td>
                    <td className="text-center" data-testid={`user-subactive-${u.username}`}>
                      {u.has_active_subpack ? (
                        <CheckCircle2 className="w-4 h-4 mx-auto text-[#10B981]" strokeWidth={2.4} aria-label="Has active Sub Pack" />
                      ) : (
                        <XCircle className="w-4 h-4 mx-auto text-[var(--sc-text-mute)]" strokeWidth={2.2} aria-label="No active Sub Pack" />
                      )}
                    </td>
                    <td className="text-center" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      {u.impersonation_allowed ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openImpersonationTab(u);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] transition-transform hover:-translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(255,182,18,0.6)]"
                          style={{
                            background: 'linear-gradient(180deg, #FFC20E 0%, #FFB612 100%)',
                            color: '#0a0a0b',
                            border: '1px solid rgba(255, 182, 18, 0.7)',
                            boxShadow: '0 4px 10px -4px rgba(255,182,18,0.5), inset 0 1px 0 rgba(255,255,255,0.35)',
                          }}
                          title={`Open ${u.username}'s dashboard in a new tab (god-admin only)`}
                          data-testid={`user-impersonate-${u.username}`}
                        >
                          <Eye className="w-3 h-3" strokeWidth={2.6} />
                          View
                        </button>
                      ) : (
                        <span className="text-[var(--sc-text-mute)] text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <AdminPagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={(p) => setPage(p)} />
      </div>

      <EditModal open={!!editing} user={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
};

export default UsersSubpage;
